import React, { useState, useEffect } from 'react';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import { useNavigate } from 'react-router-dom';

const API_URL2 = process.env.REACT_APP_API_URL;

const Students = () => {
  // State for search query, selected column, table data, and user role
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('guardianName');
  const [tableData, setTableData] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedContractForSend, setSelectedContractForSend] = useState(null);
  const [sendData, setSendData] = useState({ email: '', whatsapp: '' });
  const [sending, setSending] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printingContractId, setPrintingContractId] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest'); // "newest" or "oldest"

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  // Check authentication and get user role
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL2}/api/auth-check`, {
          method: 'GET',
          credentials: 'include', // Include cookies
        });

        const data = await response.json();

        if (data.success) {
          setUserRole(data.user.role);
          setUserPermissions(data.user.permissions || {});
        } else {
          // Redirect to login if not authenticated
          navigate('/login');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL2}/api/settings`);
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Fetch contracts data
  useEffect(() => {
    if (userRole) {
      fetchContracts();
    }
  }, [userRole]);

  // Reset to first page when search query or sort order changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedColumn, sortOrder]);

  // Fetch contracts when pagination or search changes
  useEffect(() => {
    if (userRole) {
      fetchContracts();
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    selectedColumn,
    sortOrder,
    userRole,
  ]);

  const fetchContracts = async () => {
    try {
      setFetchingData(true);

      // Build query parameters for pagination and search
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortOrder: sortOrder, // Add sort order parameter
      });

      // Add search parameters if search query exists
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
        // Map frontend column names to backend field paths
        const columnMapping = {
          guardianName: 'guardian.name',
          guardianId: 'guardian.idNumber',
          relationship: 'guardian.relationship',
          registeredPhone: 'guardian.absherMobileNumber',
          otherPhone: 'guardian.additionalMobileNumber',
          address: 'guardian.residentialAddress',
          contractEditor: 'contractEditor.name',
          editorId: 'contractEditor.idNumber',
          studentName: 'student.name',
          studentId: 'student.idNumber',
        };
        params.append(
          'searchColumn',
          columnMapping[selectedColumn] || 'guardian.name'
        );
      }

      const response = await fetch(
        `${API_URL2}/api/admin/ViewContarcts?${params}`,
        {
          method: 'GET',
          credentials: 'include', // Include cookies for authentication
        }
      );

      const data = await response.json();

      if (data.success) {
        const formattedData = data.data.map((contract) => ({
          _id: contract._id,
          guardianName: contract.guardian.name,
          guardianId: contract.guardian.idNumber,
          relationship: contract.guardian.relationship,
          registeredPhone: contract.guardian.absherMobileNumber,
          otherPhone: contract.guardian.additionalMobileNumber,
          address: contract.guardian.residentialAddress,
          contractEditor: contract.contractEditor.name,
          editorId: contract.contractEditor.idNumber,
          studentName: contract.student.name,
          studentId: contract.student.idNumber,
        }));
        setTableData(formattedData);

        // Update pagination info from backend response
        if (data.pagination) {
          setTotalItems(data.pagination.totalItems);
          setTotalPages(data.pagination.totalPages);
        }
      } else {
        console.error('Failed to fetch contracts:', data.message);
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
    } finally {
      setFetchingData(false);
    }
  };

  // Since backend now handles pagination and search, we use tableData directly
  const currentData = tableData;

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show limited pages with ellipsis
      if (currentPage <= 3) {
        // Show first 3 pages + ellipsis + last page
        for (let i = 1; i <= 3; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show first page + ellipsis + last 3 pages
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page + ellipsis + current page + ellipsis + last page
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleEdit = (id) => {
    navigate(`/edit-contract/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد أنك تريد حذف هذا العقد؟')) {
      try {
        const response = await fetch(`${API_URL2}/api/admin/delete/${id}`, {
          method: 'DELETE',
          credentials: 'include', // Include cookies for authentication
        });

        const data = await response.json();

        if (data.success) {
          setTableData(tableData.filter((contract) => contract._id !== id));
          alert('تم حذف العقد بنجاح!');
        } else {
          alert(`فشل حذف العقد: ${data.message}`);
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('خطأ في الاتصال بالخادم.');
      }
    }
  };

  const handlePrint = async (id, format) => {
    try {
      // Show floating loader
      setIsPrinting(true);
      setPrintingContractId(id);

      const response = await fetch(`${API_URL2}/api/admin/print/${id}`, {
        method: 'POST', // Changed to POST to send body
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ format }), // Send format in body: 'pdf' or 'docx'
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error message:', errorData.message);
        throw new Error(errorData.message);
      }

      const blob = await response.blob();

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `contract-${id}.${format === 'pdf' ? 'pdf' : 'docx'}`;

      if (contentDisposition) {
        // Try to extract UTF-8 encoded filename (RFC 5987: filename*=UTF-8''...)
        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''(.+?)(?:;|$)/i
        );
        if (utf8Match && utf8Match[1]) {
          try {
            filename = decodeURIComponent(utf8Match[1]);
          } catch (e) {
            console.warn('Failed to decode UTF-8 filename, trying fallback');
          }
        }

        // Fallback to regular filename if UTF-8 parsing failed
        if (!utf8Match) {
          const filenameMatch =
            contentDisposition.match(/filename="?([^"]+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
      }

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
      alert(`Failed to download the contract. Error: ${error.message}`);
    } finally {
      // Hide floating loader
      setIsPrinting(false);
      setPrintingContractId(null);
    }
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <>
        <Header />
        <div className="container mt-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">جاري التحقق من الصلاحيات...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <div dir="rtl" className="container mt-5">
        {/* Header */}
        <h1 className="text-center mb-4">قائمة عقود التسجيل للطلاب</h1>

        {/* Action Buttons - Show based on user role */}
        <div className="mb-4">
          {/* Add Button - Super Admin and Admin with Permission */}
          {(userRole === 'super_admin' ||
            (userRole === 'admin' && userPermissions.add)) && (
            <button
              className="btn btn-primary me-2"
              onClick={() => navigate('/path-selection')}
            >
              + انشاء جديد
            </button>
          )}

          {/* Super Admin Only Buttons */}
          {userRole === 'super_admin' && (
            <>
              <button
                className="btn btn-success me-2"
                onClick={() => navigate('/create-admin')}
              >
                ادارة المديرين
              </button>

              <button
                className="btn btn-danger me-2"
                onClick={() => navigate('/edit-contract-content')}
              >
                ادارة العقود
              </button>

              <button
                className="btn btn-warning me-2"
                onClick={() => navigate('/settings')}
              >
                <i className="fas fa-cog"></i> الإعدادات
              </button>
            </>
          )}
        </div>

        {/* Search Field */}
        <div className="d-flex mb-4 gap-1 justify-content-start">
          <input
            type="text"
            className="form-control w-50"
            placeholder="ابحث في الجدول"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="form-control w-25"
            value={selectedColumn}
            onChange={(e) => setSelectedColumn(e.target.value)}
          >
            <option value="guardianName">اسم ولى الامر</option>
            <option value="guardianId">رقم الهوية/الاقامة (ولى الامر)</option>
            <option value="relationship">صله القرابة</option>
            <option value="registeredPhone">رقم الجوال المسجل فـي أبشر</option>
            <option value="otherPhone">رقم جوال اخر</option>
            <option value="address">العنوان</option>
            <option value="contractEditor">اسم محرر العقد</option>
            <option value="editorId">رقم هويه/الاقامه لمحرر العقد</option>
            <option value="studentName">اسم الطالب</option>
            <option value="studentId">رقم هويه/الاقامة للطالب</option>
          </select>

          {/* Sort Order Dropdown */}
          <select
            className="form-control w-20"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">الأحدث أولاً</option>
            <option value="oldest">الأقدم أولاً</option>
          </select>
        </div>

        {/* Pagination Info and Controls */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <label className="mb-0">عرض:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-muted">
              من {(currentPage - 1) * itemsPerPage + 1} إلى{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} من {totalItems}{' '}
              نتيجة
            </span>
          </div>

          {totalPages > 1 && (
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                الأولى
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                السابق
              </button>

              {/* Page Numbers */}
              <div className="d-flex gap-1">
                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    className={`btn btn-sm ${
                      page === currentPage
                        ? 'btn-primary'
                        : page === '...'
                        ? 'btn-outline-secondary disabled'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() =>
                      typeof page === 'number' && handlePageChange(page)
                    }
                    disabled={page === '...'}
                    style={{ minWidth: '40px' }}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                التالي
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                الأخيرة
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="table-responsive">
          {fetchingData && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">جاري تحميل البيانات...</p>
            </div>
          )}

          {!fetchingData && currentData.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">لا توجد بيانات مطابقة</p>
            </div>
          )}

          {!fetchingData && currentData.length > 0 && (
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>تاريخ الإنشاء</th>
                  <th>اسم ولى الامر</th>
                  <th>رقم الهوية/الاقامة (ولى الامر)</th>
                  <th>رقم الجوال المسجل فـي أبشر</th>
                  <th>رقم جوال اخر</th>
                  <th>اسم محرر العقد</th>
                  <th>رقم هويه/الاقامه لمحرر العقد</th>
                  <th>اسم الطالب</th>
                  <th>رقم هويه/الاقامة للطالب</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((student, index) => (
                  <tr key={student._id || index}>
                    <td>
                      {new Date(
                        parseInt(student._id.substring(0, 8), 16) * 1000
                      )
                        .toLocaleDateString('ar-EG')
                        .split('/')
                        .join('-')}
                    </td>
                    <td>{student.guardianName}</td>
                    <td>{student.guardianId}</td>
                    <td>{student.registeredPhone}</td>
                    <td>{student.otherPhone}</td>
                    <td>{student.contractEditor}</td>
                    <td>{student.editorId}</td>
                    <td>{student.studentName}</td>
                    <td>{student.studentId}</td>
                    <td>
                      {/* Super Admin Only Buttons or Admin with Permissions */}
                      {(userRole === 'super_admin' || userRole === 'admin') && (
                        <>
                          {(userRole === 'super_admin' ||
                            userPermissions.edit) && (
                            <button
                              onClick={() => handleEdit(student._id)}
                              className="btn btn-warning me-1"
                              style={{
                                fontSize: '10px',
                                width: '90px',
                                margin: '1px',
                                height: '20px',
                                lineHeight: '1',
                              }}
                            >
                              تعديل
                            </button>
                          )}
                          {(userRole === 'super_admin' ||
                            userPermissions.delete) && (
                            <button
                              onClick={() => handleDelete(student._id)}
                              className="btn btn-danger me-1"
                              style={{
                                margin: '1px',
                                width: '90px',
                                height: '20px',
                                fontSize: '10px',
                                lineHeight: '1',
                              }}
                            >
                              حذف
                            </button>
                          )}
                        </>
                      )}

                      {/* Print Buttons - Side by Side */}
                      <div style={{ display: 'inline-flex', gap: '2px' }}>
                        {/* PDF Button - Available to All Users with Permission */}
                        {(userRole === 'super_admin' ||
                          userPermissions.savePdf) && (
                          <button
                            onClick={() => handlePrint(student._id, 'pdf')}
                            className="btn"
                            disabled={isPrinting}
                            style={{
                              margin: '1px',
                              width: '45px',
                              height: '20px',
                              fontSize: '10px',
                              lineHeight: '1',
                              backgroundColor: '#2E6F40',
                              color: 'white',
                              border: 'none',
                            }}
                          >
                            PDF
                          </button>
                        )}

                        {/* Word Button - Super Admin Only or Admin with Permission */}
                        {(userRole === 'super_admin' ||
                          userPermissions.saveWord) && (
                          <button
                            onClick={() => handlePrint(student._id, 'docx')}
                            className="btn btn-primary"
                            disabled={isPrinting}
                            style={{
                              margin: '1px',
                              width: '45px',
                              height: '20px',
                              fontSize: '10px',
                              lineHeight: '1',
                            }}
                          >
                            Word
                          </button>
                        )}
                      </div>

                      {/* Send Buttons */}
                      <div
                        style={{
                          display: 'inline-flex',
                          gap: '2px',
                          marginRight: '2px',
                        }}
                      >
                        {/* WhatsApp Button */}
                        {settings?.whatsappEnabled &&
                          (userRole === 'super_admin' ||
                            userPermissions.sendWhatsapp) && (
                            <button
                              onClick={() => {
                                setSelectedContractForSend(student);
                                setSendData({
                                  ...sendData,
                                  whatsapp: student.registeredPhone || '',
                                });
                                setShowWhatsAppModal(true);
                              }}
                              className="btn btn-success"
                              style={{
                                margin: '1px',
                                width: '45px',
                                height: '20px',
                                fontSize: '12px',
                                lineHeight: '1',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="إرسال واتساب"
                            >
                              <i className="fab fa-whatsapp"></i>
                            </button>
                          )}

                        {/* Email Button */}
                        {settings?.emailEnabled &&
                          (userRole === 'super_admin' ||
                            userPermissions.sendEmail) && (
                            <button
                              onClick={() => {
                                setSelectedContractForSend(student);
                                setSendData({ ...sendData, email: '' });
                                setShowEmailModal(true);
                              }}
                              className="btn btn-info"
                              style={{
                                margin: '1px',
                                width: '45px',
                                height: '20px',
                                fontSize: '12px',
                                lineHeight: '1',
                                padding: 0,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="إرسال إيميل"
                            >
                              <i className="fas fa-envelope"></i>
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Bottom Pagination Info */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <div className="text-muted">
              الصفحة {currentPage} من {totalPages} - إجمالي النتائج:{' '}
              {totalItems}
            </div>
          </div>
        )}
      </div>

      {/* Floating Print Loader */}
      {isPrinting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '30px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%',
            }}
          >
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: '3rem', height: '3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="mb-2" style={{ color: '#0B3D2E' }}>
              جاري إنشاء العقد...
            </h5>
            <p className="text-muted mb-0">
              يرجى الانتظار بينما نقوم بإنشاء وتحميل العقد
            </p>
            {printingContractId && (
              <small className="text-muted d-block mt-2">
                رقم العقد: {printingContractId}
              </small>
            )}
          </div>
        </div>
      )}

      <Footer />

      {/* Email Modal */}
      {showEmailModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  إرسال العقد عبر البريد الإلكتروني
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmailModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">البريد الإلكتروني</label>
                  <input
                    type="email"
                    className="form-control"
                    value={sendData.email}
                    onChange={(e) =>
                      setSendData({ ...sendData, email: e.target.value })
                    }
                    placeholder="example@email.com"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEmailModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!sendData.email)
                      return alert('الرجاء إدخال البريد الإلكتروني');
                    setSending(true);
                    try {
                      // First get the PDF blob
                      const printResponse = await fetch(
                        `${API_URL2}/api/admin/print/${selectedContractForSend._id}`,
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ format: 'pdf' }),
                          credentials: 'include',
                        }
                      );
                      const blob = await printResponse.blob();

                      const formData = new FormData();
                      formData.append(
                        'name',
                        selectedContractForSend.guardianName
                      );
                      formData.append('email', sendData.email);
                      formData.append(
                        'contractId',
                        selectedContractForSend._id
                      );
                      formData.append('contractFile', blob, 'contract.pdf');

                      const response = await fetch(
                        `${API_URL2}/api/send-email`,
                        {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        }
                      );
                      const data = await response.json();
                      if (data.success) {
                        alert('تم الإرسال بنجاح');
                        setShowEmailModal(false);
                      } else {
                        alert(data.error || 'فشل الإرسال');
                      }
                    } catch (error) {
                      console.error(error);
                      alert('حدث خطأ أثناء الإرسال');
                    } finally {
                      setSending(false);
                    }
                  }}
                  disabled={sending}
                >
                  {sending ? 'جاري الإرسال...' : 'إرسال'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">إرسال العقد عبر واتساب</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowWhatsAppModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    رقم الواتساب (مع مفتاح الدولة)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={sendData.whatsapp}
                    onChange={(e) =>
                      setSendData({ ...sendData, whatsapp: e.target.value })
                    }
                    placeholder="+966500000000"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowWhatsAppModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={async () => {
                    if (!sendData.whatsapp)
                      return alert('الرجاء إدخال رقم الواتساب');
                    setSending(true);
                    try {
                      // First get the PDF blob
                      const printResponse = await fetch(
                        `${API_URL2}/api/admin/print/${selectedContractForSend._id}`,
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ format: 'pdf' }),
                          credentials: 'include',
                        }
                      );
                      const blob = await printResponse.blob();

                      const formData = new FormData();
                      formData.append(
                        'name',
                        selectedContractForSend.guardianName
                      );
                      formData.append('whatsapp', sendData.whatsapp);
                      formData.append(
                        'contractId',
                        selectedContractForSend._id
                      );
                      formData.append('contractFile', blob, 'contract.pdf');

                      const response = await fetch(
                        `${API_URL2}/api/send-whatsapp`,
                        {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        }
                      );
                      const data = await response.json();
                      if (data.success) {
                        alert('تم الإرسال بنجاح');
                        setShowWhatsAppModal(false);
                      } else {
                        alert(data.error || 'فشل الإرسال');
                      }
                    } catch (error) {
                      console.error(error);
                      alert('حدث خطأ أثناء الإرسال');
                    } finally {
                      setSending(false);
                    }
                  }}
                  disabled={sending}
                >
                  {sending ? 'جاري الإرسال...' : 'إرسال'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Students;
